import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';

import { formatMeasurement } from 'constants/unitOfMeasure';

import { getPreferredUnitSystem } from 'store/session/selectors';

import { inchesToMm, mmToInches } from 'utils/helpers';

import { UnitSystemContext } from './context';

type FormatOptions = {
  decimalPoints?: number;
  includeUnits?: boolean;
};

type FormatWeightOptions = {
  weight: number; // weight in pounds
  dimensionalPrecision: number;
  displayScale?: number;
};

export const usePreferredUnitSystem = () => {
  const unitSystemSetting: PreferredUnitSystem = useSelector(
    getPreferredUnitSystem,
  );

  const unitSystemContext = useContext(UnitSystemContext);

  const preferredUnitSystem =
    unitSystemContext?.preferredUnitSystem ?? unitSystemSetting;

  const formatLength = useCallback(
    (mmValue: number, options: FormatOptions = {}) => {
      const { decimalPoints, includeUnits = true } = options;
      return formatMeasurement({
        value: mmValue,
        unitSystem: preferredUnitSystem,
        includeUnits,
        measure: 'length',
        decimalPoints,
      });
    },
    [preferredUnitSystem],
  );

  const formatArea = useCallback(
    (mmValue: number, options: FormatOptions = {}) => {
      const { decimalPoints, includeUnits = true } = options;
      return formatMeasurement({
        value: mmValue,
        unitSystem: preferredUnitSystem,
        includeUnits,
        measure: 'area',
        decimalPoints,
      });
    },
    [preferredUnitSystem],
  );

  const formatVolume = useCallback(
    (mmValue: number, options: FormatOptions = {}) => {
      const { decimalPoints, includeUnits = true } = options;
      return formatMeasurement({
        value: mmValue,
        unitSystem: preferredUnitSystem,
        includeUnits,
        measure: 'volume',
        decimalPoints,
      });
    },
    [preferredUnitSystem],
  );

  const formatWeight = useCallback(
    ({
      weight,
      dimensionalPrecision,
      displayScale = 1.0,
    }: FormatWeightOptions): string => {
      if (weight == null) {
        return '---';
      }

      // Apply display scale (cubed for volume scaling)
      const scaledWeight = weight * Math.pow(displayScale, 3);

      return formatMeasurement({
        value: scaledWeight, // weight in pounds
        unitSystem: preferredUnitSystem,
        includeUnits: true,
        measure: 'weight',
        decimalPoints: dimensionalPrecision,
      });
    },
    [preferredUnitSystem],
  );

  const mmToPreferredUnits = useCallback(
    (mmValue: number, power?: number, precision?: number) => {
      return preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? mmToInches(mmValue, power, precision)
        : mmValue;
    },
    [preferredUnitSystem],
  );

  const preferredUnitsToMm = useCallback(
    (value: number, power?: number) => {
      return preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? inchesToMm(value, power)
        : value;
    },
    [preferredUnitSystem],
  );

  return {
    preferredUnitSystem,
    formatArea,
    formatLength,
    formatVolume,
    formatWeight,
    mmToPreferredUnits,
    preferredUnitsToMm,
  };
};
